/**
 * SessionView — Pure rendering component for session content.
 *
 * Receives all state from useSessionStore (cards, orderedCardIds, tasks)
 * and delegates rendering to existing block renderers. No state management,
 * no event processing, no SSE connections.
 *
 * Replaces LiveSessionView (1930 lines) with ~250 lines.
 */

import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronDown, ChevronUp, Sparkles, Loader2, X } from 'lucide-react';
import PersistentHtmlRenderer from './PersistentHtmlRenderer';
import { renderTemplate, extractCardTitle } from '../services/templateRenderer';
import ChatBubble from './session/ChatBubble';
import SessionBlockCard from './session/SessionBlockCard';
import A2uiSurfaceCard from './session/A2uiSurfaceCard';
import MailCard from './session/mail/MailCard';
import { isMailTemplate, mailLinkHref } from './session/mail/mailUtils';
import { api } from '../services/api';
import ProcessProgressBar from './session/ProcessProgressBar';
import CustomInputBar from './session/CustomInputBar';
import ShimmerPlaceholder from './session/ShimmerPlaceholder';
import { getShortTitle, stripHiddenTags } from '../utils/text';
import MdStreamingRenderer, { STREAMABLE_TEMPLATES } from './MdStreamingRenderer';

// ── Loading spinner HTML for HTML blocks still streaming ──
const LOADING_SPINNER_HTML = '<div style="display:flex;align-items:center;gap:8px;padding:16px;color:rgba(0,0,0,0.4);font-family:system-ui,sans-serif;font-size:14px;"><div style="width:16px;height:16px;border:2px solid rgba(0,0,0,0.1);border-top-color:rgba(168,85,247,0.7);border-radius:50%;animation:spin 0.8s linear infinite;"></div> Preparing...</div><style>@keyframes spin{to{transform:rotate(360deg)}}</style>';

// ── Spring presets ──
const SPRING_CARD = { type: 'spring', stiffness: 380, damping: 32 };
const SPRING_FAST = { type: 'spring', stiffness: 450, damping: 32 };

function TemplateCardRenderer({ template, data, isStreaming, onAction }) {
  const [html, setHtml] = useState(null);

  // During streaming for md-* templates, render inline (instant updates).
  // After finalize, render via Handlebars + iframe (full styling).
  const useInlineRenderer = isStreaming && STREAMABLE_TEMPLATES.has(template);

  useEffect(() => {
    if (useInlineRenderer) return; // skip heavy render during streaming
    renderTemplate(template, data).then(result => {
      if (result) setHtml(result);
    });
  }, [template, data, useInlineRenderer]);

  if (useInlineRenderer) {
    return <MdStreamingRenderer template={template} data={data} />;
  }

  if (!html) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="text-black/15 animate-spin" />
      </div>
    );
  }

  return (
    <PersistentHtmlRenderer
      streamingChunks={[html]}
      isStreaming={false}
      onAction={onAction}
    />
  );
}

// ═══════════════════════════════════════════════════════════
// CardRenderer — dispatches a single card to the right renderer
// ═══════════════════════════════════════════════════════════

function CardRenderer({ card, onCardAction, onFollowUp, onA2uiAction }) {
  const { template, data, status } = card;
  const isDone = status === 'finalized' || status === 'done';
  const isStreaming = !isDone;

  // 0. File error from server (e.g. missing local file after GCS migration)
  if (data?._fileError) {
    return <div className="vi-file-error">{data._fileError}</div>;
  }

  // 1. Bubble template → ChatBubble
  if (template === 'bubble') {
    return (
      <div className="px-4 py-1">
        <ChatBubble role={data?.role || 'agent'} text={stripHiddenTags(data?.content || '')} />
      </div>
    );
  }

  if (isMailTemplate(template)) {
    return (
      <MailCard
        card={card}
        onA2uiAction={onA2uiAction}
        onOpenTarget={(target) => {
          const href = mailLinkHref(target);
          if (href) window.open(href, '_blank', 'noopener');
        }}
      />
    );
  }

  // 1a. a2ui SURFACE (v0.10 `a2ui`): the turn's rich answer is ONE surface —
  // an append-only a2ui 1.0 envelope log. Sniffed BEFORE the html shape-test
  // for the same reason as `session_block`: an envelope's data could carry an
  // `html`-shaped field. The composer (`onFollowUp`) is the standard v1
  // `follow_up` return channel; a surface mounted without one reports the tap.
  if (template === 'a2ui_surface' || Array.isArray(data?.envelopes)) {
    return <A2uiSurfaceCard card={card} onCardAction={onCardAction} onFollowUp={onFollowUp} />;
  }

  // 1b. NATIVE BLOCK (v0.9 `native-ir`): the card carries a ViewIR node, not
  // HTML. It is sniffed BEFORE the `data?.html` branch below on purpose — that
  // branch is a shape sniff rather than a template test, and a block whose
  // model-authored data happened to hold an `html` field would otherwise be
  // handed to the iframe renderer and render as raw markup. The node is the
  // test; the template name is the belt.
  if (template === 'session_block' || data?.node?.type) {
    // `onFollowUp` is the composer, and it is OPTIONAL: a chip or an
    // `openSession` button means "start a turn", and a surface mounted without
    // a composer (App.jsx's live session) simply reports the tap instead.
    return <SessionBlockCard card={card} onCardAction={onCardAction} onFollowUp={onFollowUp} />;
  }

  // 2. Freeform HTML path: card has html slot (also `debug-html`, same renderer)
  if (template === 'freeform-html' || template === 'debug-html' || data?.html) {
    const htmlContent = data?.html || '';
    if (!htmlContent && isDone) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <p className="font-light" style={{ fontSize: 'var(--text-sm)', color: 'rgba(0,0,0,0.2)' }}>
            Content unavailable
          </p>
        </div>
      );
    }
    if (!htmlContent && isStreaming) {
      return (
        <PersistentHtmlRenderer
          streamingChunks={[LOADING_SPINNER_HTML]}
          isStreaming={true}
          onAction={onCardAction}
        />
      );
    }
    return (
      <PersistentHtmlRenderer
        streamingChunks={htmlContent ? [htmlContent] : []}
        isStreaming={isStreaming}
        onAction={onCardAction}
      />
    );
  }

  // 3. Template rendering path: render via Handlebars + iframe
  if (data) {
    return <TemplateCardRenderer template={template} data={data} isStreaming={isStreaming} onAction={onCardAction} />;
  }

  // 4. Fallback: shimmer placeholder
  return <ShimmerPlaceholder lines={3} className="px-5 py-4" />;
}


// ═══════════════════════════════════════════════════════════
// CanvasCard — wrapper with collapse/expand for non-HTML blocks
// ═══════════════════════════════════════════════════════════

function CanvasCard({ card, expanded, onToggle, onCardAction, onFollowUp, isLatest }) {
  const { template, data, status } = card;
  const isDone = status === 'finalized' || status === 'done';
  const title = extractCardTitle(template, data);

  // Collapsed view
  if (!expanded && !isLatest) {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={SPRING_CARD}
        className="mb-1"
        style={{ willChange: 'transform, opacity' }}
      >
        <button
          onClick={onToggle}
          className="w-full text-left py-3 hover:opacity-70 transition-opacity"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate" style={{ fontSize: 'var(--text-sm)', color: 'rgba(0,0,0,0.5)' }}>
                {title}
              </p>
            </div>
            <ChevronDown size={14} style={{ color: 'rgba(0,0,0,0.12)' }} className="shrink-0" />
          </div>
        </button>
      </motion.div>
    );
  }

  // Expanded view
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4, scale: 0.98 }}
      transition={SPRING_CARD}
      className="mb-4"
      style={{ willChange: 'transform, opacity' }}
    >
      {/* Collapse handle for non-latest done cards */}
      {!isLatest && isDone && (
        <div className="flex items-center justify-between mb-2 pb-1" style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
          <button
            onClick={onToggle}
            className="flex items-center gap-1 hover:opacity-70 transition-opacity"
            style={{ fontSize: 'var(--text-xs)', color: 'rgba(0,0,0,0.25)' }}
          >
            <ChevronUp size={12} />
            <span>Collapse</span>
          </button>
          {card.createdAt && (
            <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.15)' }}>
              {new Date(card.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      )}

      <CardRenderer card={card} onCardAction={onCardAction} onFollowUp={onFollowUp} />
    </motion.div>
  );
}


// ═══════════════════════════════════════════════════════════
// SessionView — Main Component
// ═══════════════════════════════════════════════════════════

export default function SessionView({
  cards,
  orderedCardIds,
  tasks,
  sessionId,
  onBack,
  onSendMessage,
  onCardAction,
  onA2uiAction,
  photos,
  intention,
  className,
}) {
  const scrollContainerRef = useRef(null);
  const [expandedCardIds, setExpandedCardIds] = useState(new Set());

  // Derive active task (any with loading/streaming status)
  const activeTask = useMemo(() => {
    if (!tasks) return null;
    return Object.values(tasks).find(
      (t) => t.status === 'loading' || t.status === 'streaming'
    ) || null;
  }, [tasks]);

  // Filter visible cards (exclude removed)
  const visibleCards = useMemo(() => {
    if (!orderedCardIds || !cards) return [];
    return orderedCardIds
      .map((id) => cards[id])
      .filter((card) => card && card.status !== 'removed');
  }, [orderedCardIds, cards]);

  // Determine if there's any canvas content
  const hasContent = visibleCards.length > 0;

  // Toggle card expanded state
  const toggleCardExpanded = useCallback((cardId) => {
    setExpandedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  }, []);

  const handleA2uiAction = useCallback(
    (envelope) => {
      if (onA2uiAction) {
        onA2uiAction(envelope);
        return;
      }
      const prompt = envelope?.action?.userMessage;
      if (!prompt || !sessionId) return;
      api.dispatchExec({ prompt, sessionId, a2uiAction: envelope })
        .catch((e) => console.warn('[session] a2ui action send failed:', e));
    },
    [onA2uiAction, sessionId],
  );

  // Handle card action → delegate to parent
  const handleCardAction = useCallback(
    (actionData) => {
      onCardAction?.(actionData);
    },
    [onCardAction],
  );

  return (
    <div
      className={`absolute inset-0 flex flex-col ${className || ''}`}
      style={{ background: '#fff' }}
    >
      {/* ═══ HEADER ═══ */}
      <div className="safe-area-top w-full flex items-center px-4 pb-2 shrink-0 z-30" style={{ background: '#fff' }}>
        <button
          onClick={onBack}
          className="p-2 rounded-full hover:bg-black/[0.04] transition-colors z-10"
          style={{ color: 'rgba(0,0,0,0.4)' }}
        >
          <ChevronLeft size={22} />
        </button>
        {intention && (
          <h1
            className="absolute left-0 right-0 text-center font-semibold tracking-wide pointer-events-none"
            style={{ fontSize: 'var(--text-base)', color: '#000' }}
          >
            {getShortTitle(intention)}
          </h1>
        )}
      </div>

      {/* ═══ SCROLLABLE CANVAS ═══ */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto pb-28"
        style={{ overscrollBehavior: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {/* Photo gallery (if photos provided) */}
        {photos?.length > 0 && (
          <div className="px-4 pb-3">
            <div
              className="relative overflow-hidden flex gap-2"
              style={{ borderRadius: 24, border: '1px solid rgba(0,0,0,0.04)', background: '#f0f0f0' }}
            >
              {photos.map((photo, i) => (
                <img
                  key={i}
                  src={photo.gcsUrl || photo.src || photo}
                  alt=""
                  className="w-full h-full object-cover"
                  style={{ maxHeight: 200 }}
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              ))}
            </div>
          </div>
        )}

        {/* Canvas blocks */}
        <div className="pt-4">
          <AnimatePresence initial={false}>
            {visibleCards.map((card, i) => {
              const isLatest = i === visibleCards.length - 1;
              const isBubble = card.template === 'bubble';
              const isHtmlType = card.template === 'freeform-html' || card.template === 'debug-html' || (!card.template && card.data?.html);

              // Thinking cards auto-collapse when a non-thinking card follows
              const isThinking = card.template === 'thinking-process' || card.template === 'thinking';
              const hasNonThinkingAfter = isThinking && visibleCards.slice(i + 1).some(
                (c) => c.template !== 'thinking-process' && c.template !== 'thinking'
              );
              const isExpanded = isThinking
                ? (hasNonThinkingAfter ? expandedCardIds.has(card.cardId) : true)
                : (isLatest || expandedCardIds.has(card.cardId));

              // Task progress for this card's task
              const taskProgress = card.taskId && tasks?.[card.taskId]?.progress;

              // Bubbles render inline
              if (isBubble) {
                return (
                  <motion.div
                    key={card.cardId}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={SPRING_FAST}
                  >
                    <CardRenderer card={card} onCardAction={handleCardAction} />
                  </motion.div>
                );
              }

              // HTML-type cards render full-width (no padding wrapper)
              if (isHtmlType) {
                return (
                  <motion.div
                    key={card.cardId}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={SPRING_CARD}
                    style={{ willChange: 'transform, opacity' }}
                  >
                    <CardRenderer card={card} onCardAction={handleCardAction} />
                    {taskProgress && (
                      <div className="flex items-center gap-1.5 mb-3 px-5">
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={SPRING_FAST}
                          className="flex items-center gap-1.5"
                          style={{ padding: '4px 0', fontSize: 12, color: 'rgba(0,0,0,0.35)' }}
                        >
                          <span style={{ fontSize: 11, color: 'rgba(168,85,247,0.5)', fontWeight: 600 }}>
                            {taskProgress.step}/{taskProgress.total}
                          </span>
                          <span>{taskProgress.message || 'Processing...'}</span>
                        </motion.div>
                      </div>
                    )}
                  </motion.div>
                );
              }

              // NATIVE BLOCK (v0.9 `native-ir`): a turn is ONE ANSWER split
              // into blocks, not a stack of separate objects — so a block gets
              // NO CanvasCard: no plate, and no per-block "Collapse" handle
              // that would chop a single answer into title rows the moment the
              // next block arrives. It sits in the same reading column
              // (`px-5`) as everything else and is spaced from the next block
              // the way two paragraphs are, which is how the html lane reads.
              const isSessionBlock = card.template === 'session_block' || Boolean(card.data?.node?.type)
                || card.template === 'a2ui_surface' || Array.isArray(card.data?.envelopes)
                || isMailTemplate(card.template);

              // Module/react cards get CanvasCard wrapper with padding
              return (
                <div key={card.cardId} className="px-5">
                  {isSessionBlock ? (
                    <motion.div
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={SPRING_CARD}
                      className="mb-4"
                      style={{ willChange: 'transform, opacity' }}
                    >
                      <CardRenderer
                        card={card}
                        onCardAction={handleCardAction}
                        onFollowUp={onSendMessage}
                        onA2uiAction={handleA2uiAction}
                      />
                    </motion.div>
                  ) : (
                    <CanvasCard
                      card={card}
                      expanded={isExpanded}
                      isLatest={isLatest}
                      onToggle={() => toggleCardExpanded(card.cardId)}
                      onCardAction={handleCardAction}
                      onFollowUp={onSendMessage}
                    />
                  )}
                  {taskProgress && (
                    <div className="flex items-center gap-1.5 mb-3 -mt-2">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={SPRING_FAST}
                        className="flex items-center gap-1.5"
                        style={{ padding: '4px 0', fontSize: 12, color: 'rgba(0,0,0,0.35)' }}
                      >
                        <span style={{ fontSize: 11, color: 'rgba(168,85,247,0.5)', fontWeight: 600 }}>
                          {taskProgress.step}/{taskProgress.total}
                        </span>
                        <span>{taskProgress.message || 'Processing...'}</span>
                      </motion.div>
                    </div>
                  )}
                </div>
              );
            })}
          </AnimatePresence>

          {/* Active task progress bar */}
          {activeTask && (
            <div className="px-5 py-2">
              <ProcessProgressBar
                completed={activeTask.progress?.step || 0}
                total={activeTask.progress?.total || 1}
              />
            </div>
          )}

          {/* Empty state — waiting */}
          {!hasContent && (
            <div className="flex flex-col items-center justify-center py-20 px-5">
              <Sparkles size={28} className="mb-3 animate-pulse" style={{ color: 'rgba(0,0,0,0.15)' }} />
              <p className="font-light" style={{ fontSize: 'var(--text-sm)', color: 'rgba(0,0,0,0.25)' }}>
                Waiting for results...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ═══ BOTTOM INPUT BAR ═══ */}
      {onSendMessage && (
        <CustomInputBar
          onSubmit={onSendMessage}
          placeholder="Ask anything..."
        />
      )}
    </div>
  );
}
